/**
 * `automation-draft` — the agreement card (TV1-2382 / TV1-2340 forks 7, 7b, 7c).
 *
 * This is not decoration on an answer. It IS the agreement: the seller reads
 * back what the assistant understood, in her own language, and only her Submit
 * turns it into an Automation Request. Without it she gets one shot and every
 * misunderstanding costs a full round-trip through a developer — which is the
 * one thing the review queue's efficiency depends on most.
 *
 * ─── Her language, never a graph ────────────────────────────────────────────
 * The card renders `display.when` / `display.then`, the seller-language strings
 * the backend produced. The compiled node graph is the REVIEWER's surface (fork
 * 8) and is deliberately absent here; a message carries exactly ONE block, so
 * this card and `RulePreviewBlock` cannot coexist on a turn anyway.
 *
 * ─── 🚨 Two things this card is OBLIGED to say ──────────────────────────────
 *  1. **The binding, echoed in her own words.** Auto-classification is an
 *     unmeasured heuristic over Spanish prose — "…de estos tres" misread as
 *     account-wide binds her entire catalogue — and her reading back
 *     `binding.sellerQuote` is the only check that exists on it. It is rendered
 *     for every sub-goal, always, never collapsed behind a disclosure.
 *  2. **An `enumerated` binding is a SNAPSHOT.** Items added later are NOT
 *     covered and nothing will tell her. Silence here is the failure mode where
 *     a rule quietly stops applying to half a growing catalogue.
 *
 * ─── Submit ─────────────────────────────────────────────────────────────────
 * Submit posts to `/ai-assistant/automation-requests/submit` — from
 * `AutomationDraftBlockConnected`, which supplies `onSubmit`; this renderer
 * itself stays free of any backend so it renders in jsdom with no provider.
 * That endpoint runs the
 * SAME `submit_automation_request` execute the model would. It does not dispatch
 * a command of its own and does not inject a synthetic user message: a direct
 * dispatch would run no tool, so the gate would have to be copied and the copy
 * would drift, and a synthetic message would make her most consequential click
 * non-deterministic.
 *
 * The disabled state here is an ECHO of the server gate, never a second gate.
 * The server refuses regardless; disabling only avoids offering a click that is
 * going to come back refused, and says which question is still open. The only
 * other things that disable it are `submitting` and `submitted` — both facts
 * about this card's own POST, never a judgement on the draft.
 *
 * ⚠️ Copy discipline: nothing on this card may imply that anything exists yet.
 * Until Submit there is no Automation Request — that is what keeps the Requests
 * tab meaning "what the system is actually working on". It binds in BOTH
 * directions: once a submit is accepted something DOES exist, so the "nothing has
 * been created yet" line is REPLACED rather than left standing next to a link to
 * the thing that was created.
 *
 * ─── Saying what happened ───────────────────────────────────────────────────
 * `outcome` is the whole of it, and a refusal and a failure are deliberately not
 * one case. A REFUSAL arrives as a resolved 200 carrying `{ ok: false, message }`
 * — the server's own answer, rendered verbatim. A FAILURE is every call that
 * produced no answer this client can read (a throw, or a 200 with no `ok`
 * discriminator), and only it gets copy of our own. Collapsing the two would
 * either put a transport error in the seller's language or bury the one sentence
 * that tells her what to fix.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Anchor, Badge, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconListCheck, IconPlugConnectedX } from '@tabler/icons-react';
import { useActiveFeatureFlags, useFeatureFlagsReady } from '@bett3r-dev/teselly-feature-flags';
import type { MessageBlockRendererProps } from '@domains/aiAssistant/state/messageBlocks';
import type { TFunction } from 'i18next';
import {
  AUTOMATION_DRAFT_REFERENCE_SET_CAP,
  type AutomationDraftPayload,
  type AutomationDraftReference,
  type SubmitAutomationRequestOutcome,
  bindingIsSnapshot,
  unresolvedClarifications
} from '@domains/aiAssistant/state/automationDraft';
import { componentsTestIds } from '@components/testids';
import classes from './AutomationDraftBlock.module.css';

export type AutomationDraftBlockPayload = AutomationDraftPayload;

export type AutomationDraftBlockProps = MessageBlockRendererProps & {
  /**
   * Submit handler. Injected rather than reached for from the store so this
   * renderer stays testable in jsdom without a live backend — and so the ONE
   * network call this card makes is visible at the registration site.
   */
  onSubmit?: ( payload: AutomationDraftPayload ) => void;
  /**
   * A submit issued from this card is still IN FLIGHT. ANDed into the button's
   * disabled state — it guards a second concurrent POST, and nothing more. It is
   * NOT what stops a second Automation Request from existing: the request id is
   * minted once per mounted card, and a repeated submit of that id is a graceful
   * no-op at the aggregate, whose `SubmitAutomationRequest` command carries
   * `.withIdempotencyCheck( idempotency.check( ..., 'exists' ))`
   * (`src/services/server/src/modules/automation/automationRequests.aggregate.ts`).
   * Owned by whoever owns `onSubmit`, since this renderer never learns when the
   * call finishes.
   */
  submitting?: boolean;
  /**
   * A submit issued from this card was ACCEPTED by the server (`ok: true`).
   * ANDed into the SAME disabled state: the control stays disabled after a
   * successful submit, so a spent card cannot be re-sent. Kept separate from
   * `submitting` because they are different facts — an in-flight button reads as
   * busy, an accepted one is spent — and separate from a REFUSAL, which also
   * resolves but must leave the control usable.
   *
   * Its holder is the sole writer of `outcome` too, and the two agree by
   * construction: `submitted` is set exactly when `outcome.kind` is `submitted`.
   */
  submitted?: boolean;
  /**
   * What the last submit did, once it is known. `undefined` while nothing has
   * been attempted AND while a submit is in flight — the card claims no outcome
   * it does not yet have. See the docblock for why a refusal and a failure are
   * two cases and not one.
   */
  outcome?: SubmitAutomationRequestOutcome;
};

/**
 * What one reference resolved to, under the seller's own quote (TV1-2453).
 * Display only — the reference itself is submitted untouched.
 *
 *  - named: entity NAMES (the server sends the id as the name when the row is
 *    gone, flagged `fields.nameUnverified`, which is shown as a hint).
 *  - set: the size, or "más de 20" for `'>cap'`; an account-scope CHANNEL set
 *    also says future channels are included. `set.channelIds` is never shown.
 *  - option: its label.
 */
const ReferenceResolution = ({ subGoalId, reference, t }: {
  subGoalId: string;
  reference: AutomationDraftReference;
  t: TFunction;
}) => {
  const { set, option, entities } = reference;
  return (
    <Text
      size="xs"
      c="dimmed"
      data-testid={componentsTestIds.aiAutomationDraftReference( subGoalId, reference.referenceId )}
    >
      {t( 'ai_automation_draft_reference_quote', { quote: reference.quote, defaultValue: '"{{quote}}":' })}
      {( entities ?? []).map(( entity, index ) => (
        <React.Fragment key={`${ entity.id }-${ index }`}>
          {index > 0 ? ', ' : ' '}
          {entity.name}
          {entity.fields?.nameUnverified === true &&
            ` ${ t( 'ai_automation_draft_reference_unverified', { defaultValue: '(sin verificar)' }) }`}
        </React.Fragment>
      ))}
      {set && ` ${ set.size === '>cap'
        ? t( 'ai_automation_draft_reference_set_over_cap', {
          cap: AUTOMATION_DRAFT_REFERENCE_SET_CAP,
          defaultValue: 'más de {{cap}}'
        })
        : t( 'ai_automation_draft_reference_set_size', { size: set.size, defaultValue: '{{size}} en total' }) }`}
      {set && set.scope === 'account' && reference.kind === 'channel' &&
        ` ${ t( 'ai_automation_draft_reference_future_channels', { defaultValue: '(incluye los que conectes)' }) }`}
      {option && ` ${ option.label }`}
    </Text>
  );
};

export const AutomationDraftBlock = ({ block, onSubmit, submitting, submitted, outcome }: AutomationDraftBlockProps ) => {
  const { t } = useTranslation( 'interactions' );
  const payload = block.payload as AutomationDraftPayload | undefined;

  /**
   * TV1-2377 slice 11 — the kill switch. Reuses the EXISTING `aiAssistant` flag
   * (already gating the navbar's assistant button); no new flag is minted.
   *
   * `useFeatureFlagsReady()` MUST be checked alongside the flag value: before the
   * account-scoped snapshot arrives, `useActiveFeatureFlags()` returns `[]` (never
   * "everything on"), so this reads FAIL-CLOSED both before and after hydration —
   * there is no window where an uninitialized store renders the card.
   */
  const flagsReady = useFeatureFlagsReady();
  const activeFlags = useActiveFeatureFlags();
  if ( !flagsReady || !activeFlags.includes( 'aiAssistant' )) return null;

  if ( !payload?.draft?.subGoals?.length ) return null;

  const { draft, unresolvedCount } = payload;
  const blocked = unresolvedCount > 0;

  return (
    <Stack gap="xs" className={classes.card} data-testid={componentsTestIds.aiAutomationDraft}>
      <Group gap="xs">
        <IconListCheck size={16} />
        <Text fw={600} size="sm">{draft.goal}</Text>
      </Group>

      {draft.subGoals.map(( subGoal ) => {
        const open = unresolvedClarifications( subGoal );
        return (
          <Stack
            key={subGoal.subGoalId}
            gap={4}
            className={classes.subGoal}
            data-testid={componentsTestIds.aiAutomationDraftSubGoal( subGoal.subGoalId )}
          >
            <Text size="sm" fw={500}>{subGoal.title}</Text>
            <Text size="sm">
              {t( 'ai_automation_draft_when_then', {
                when: subGoal.display.when,
                then: subGoal.display.then,
                defaultValue: 'Cuando {{when}}, entonces {{then}}.'
              })}
            </Text>

            {/* Always rendered — see the docblock. The seller reading her own
                words back is the only check on the classification. */}
            <Text
              size="xs"
              c="dimmed"
              data-testid={componentsTestIds.aiAutomationDraftBinding( subGoal.subGoalId )}
            >
              {subGoal.binding
                ? t( 'ai_automation_draft_binding', {
                  quote: subGoal.binding.sellerQuote,
                  defaultValue: 'Se aplica a: "{{quote}}"'
                })
                : t( 'ai_automation_draft_binding_missing', {
                  defaultValue: 'Falta definir a qué se aplica.'
                })}
            </Text>

            {( subGoal.references ?? []).map(( reference, index ) => (
              <ReferenceResolution
                // Index-suffixed: a hand-built draft can still carry duplicate referenceIds.
                key={`${reference.referenceId}-${index}`}
                subGoalId={subGoal.subGoalId}
                reference={reference}
                t={t}
              />
            ))}

            {bindingIsSnapshot( subGoal.binding ) && (
              <Text
                size="xs"
                c="orange"
                data-testid={componentsTestIds.aiAutomationDraftSnapshotWarning( subGoal.subGoalId )}
              >
                {t( 'ai_automation_draft_snapshot_warning', {
                  defaultValue: 'Solo estos productos. Los que agregues después no quedan incluidos.'
                })}
              </Text>
            )}

            {open.map(( clarification ) => (
              <Text
                key={clarification.id}
                size="xs"
                c="dimmed"
                data-testid={componentsTestIds.aiAutomationDraftClarification( clarification.id )}
              >
                {clarification.question}
              </Text>
            ))}
          </Stack>
        );
      })}

      {blocked && (
        <Alert
          variant="light"
          color="yellow"
          icon={<IconAlertTriangle size={16} />}
          data-testid={componentsTestIds.aiAutomationDraftBlocked}
        >
          {t( 'ai_automation_draft_blocked', {
            count: unresolvedCount,
            defaultValue: 'Faltan {{count}} respuestas antes de poder enviarlo.'
          })}
        </Alert>
      )}

      {/* The server's OWN words, rendered verbatim. A refusal names the one
          thing she has to fix, and paraphrasing it here would put this card in
          the business of restating a gate it does not own. */}
      {outcome?.kind === 'refused' && (
        <Alert
          variant="light"
          color="yellow"
          icon={<IconAlertTriangle size={16} />}
          data-testid={componentsTestIds.aiAutomationDraftRefused}
        >
          {outcome.message}
        </Alert>
      )}

      {/* Every call that produced no readable answer — a throw, or a 200 this
          client cannot narrow. Never a refusal: that one has words of its own. */}
      {outcome?.kind === 'failed' && (
        <Alert
          variant="light"
          color="red"
          icon={<IconPlugConnectedX size={16} />}
          data-testid={componentsTestIds.aiAutomationDraftFailed}
        >
          {t( 'ai_automation_draft_failed', {
            defaultValue: 'No pudimos enviarlo. Probá de nuevo.'
          })}
        </Alert>
      )}

      <Group justify="space-between">
        <Badge variant="light" size="sm">
          {t( 'ai_automation_draft_sub_goal_count', {
            count: draft.subGoals.length,
            defaultValue: '{{count}} objetivos'
          })}
        </Badge>
        <Button
          size="xs"
          // `loading` renders the spinner AND disables the control on its own;
          // `submitting` stays in the disabled expression so the two facts that
          // spend the button remain readable in one place.
          loading={submitting}
          disabled={blocked || submitting || submitted}
          onClick={() => onSubmit?.( payload )}
          data-testid={componentsTestIds.aiAutomationDraftSubmit}
        >
          {t( 'ai_automation_draft_submit', { defaultValue: 'Enviar' })}
        </Button>
      </Group>

      {/* 🚨 The copy discipline, in both directions. Before an accepted submit
          the card must say nothing exists; after one, something DOES, and
          leaving that line up would be the same dishonesty inverted. */}
      {outcome?.kind === 'submitted' ? (
        <Stack gap={2}>
          <Text className={classes.honesty} data-testid={componentsTestIds.aiAutomationDraftSubmitted}>
            {t( 'ai_automation_draft_submitted', {
              defaultValue: 'Listo, lo enviamos. Tu pedido ya quedó registrado.'
            })}
          </Text>
          {/* A plain `href`, not a router `Link`: this renderer is mounted with
              no provider of any kind (see the docblock), and `useNavigate`
              outside a Router throws. The path is the one the SERVER returned. */}
          {outcome.url && (
            <Anchor
              href={outcome.url}
              size="xs"
              data-testid={componentsTestIds.aiAutomationDraftRequestLink}
            >
              {t( 'ai_automation_draft_track_request', { defaultValue: 'Seguí tu pedido' })}
            </Anchor>
          )}
        </Stack>
      ) : (
        <Text className={classes.honesty} data-testid={componentsTestIds.aiAutomationDraftNotSubmitted}>
          {t( 'ai_automation_draft_not_submitted', {
            defaultValue: 'Todavía no se creó nada. Se crea recién cuando lo envíes.'
          })}
        </Text>
      )}
    </Stack>
  );
};
